import { DISPLAY_NAME_MESSAGES, validateDisplayName } from '@words-happen/display-name';
import { validateEmail } from '../sign-in/sign-in-validation';

// The display name rules are shared with words-happen-gql.
export {
  countCharacters,
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  normaliseDisplayName,
  validateDisplayName,
} from '@words-happen/display-name';

export interface RegistrationDetails {
  displayName: string;
  email: string;
  password: string;
}

export type RegistrationErrors = Partial<Record<keyof RegistrationDetails, string>>;

/** The Identity Platform password policy (ENG-845, NIST SP 800-63B-4): 15–128 characters, no composition rules. */
export const PASSWORD_MIN_LENGTH = 15;
/** Identity Platform rejects anything longer. */
export const PASSWORD_MAX_LENGTH = 128;

/** The password policy in the form password managers read from the input's passwordrules attribute. */
export const PASSWORD_RULES = `minlength: ${PASSWORD_MIN_LENGTH}; maxlength: ${PASSWORD_MAX_LENGTH};`;

export const REGISTER_MESSAGES = {
  displayNameRequired: DISPLAY_NAME_MESSAGES.required,
  displayNameTooShort: DISPLAY_NAME_MESSAGES.tooShort,
  displayNameTooLong: DISPLAY_NAME_MESSAGES.tooLong,
  displayNameInvalidCharacters: DISPLAY_NAME_MESSAGES.invalidCharacters,
  passwordHint: `${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters`,
  passwordPolicy: `Use ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters`,
  passwordRequired: 'Choose a password',
  passwordTooShort: `Use at least ${PASSWORD_MIN_LENGTH} characters`,
  passwordTooLong: `Use ${PASSWORD_MAX_LENGTH} characters or fewer`,
} as const;

/**
 * A password's length as Identity Platform's policy counts it: UTF-16 code units, as the Firebase SDK's
 * validatePassword does, so an emoji outside the Basic Multilingual Plane counts as 2. infrastructure/identity's
 * smoke test checks this against the development project.
 */
export const countPasswordLength = (value: string): number => value.length;

/** Required, and within the password policy. Taken as typed: spaces count. */
export const validateNewPassword = (value: string): string | undefined => {
  if (!value) return REGISTER_MESSAGES.passwordRequired;
  const length = countPasswordLength(value);
  if (length < PASSWORD_MIN_LENGTH) return REGISTER_MESSAGES.passwordTooShort;
  if (length > PASSWORD_MAX_LENGTH) return REGISTER_MESSAGES.passwordTooLong;
  return undefined;
};

export const REGISTER_VALIDATORS = {
  displayName: validateDisplayName,
  email: validateEmail,
  password: validateNewPassword,
} as const;

export const validateRegistration = (details: RegistrationDetails): RegistrationErrors => {
  const errors: RegistrationErrors = {};
  for (const field of Object.keys(REGISTER_VALIDATORS) as (keyof RegistrationDetails)[]) {
    const error = REGISTER_VALIDATORS[field](details[field]);
    if (error) errors[field] = error;
  }
  return errors;
};
